import type { CSSProperties, FC, ReactNode } from 'react'

import { Fragment, useEffect, useRef, useState } from 'react'

import type { Command, KeyAction, KeyDef } from '@/components/landing/console/data'

import {
    BOOT_SPLASH,
    COMMANDS,
    GALLERY,
    KEY_ROWS,
    SNARK,
    TRY_LIST
} from '@/components/landing/console/data'
import { AppWindow } from '@/components/landing/console/windows'

const BG_SRC = '/assets/console/desk.png'
const CARET = '▍'
const MAX_INPUT = 320

type Phase = 'booting' | 'idle' | 'responding' | 'viewing'

interface ScrollEntry {
    cmd: string
    reply: string
}

interface ZoomData {
    scale: number
    dx: number
    dy: number
    originX: number
    originY: number
}

const rand = (min: number, max: number): number => Math.random() * (max - min) + min

const isEditable = (el: Element | null): boolean => {
    if (!el) return false
    const tag = el.tagName
    return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (el as HTMLElement).isContentEditable === true
    )
}

const mapPhysical = (key: string): { id: string; action: KeyAction; label: string } | null => {
    if (key.length === 1) {
        if (/[a-zA-Z]/.test(key)) {
            const up = key.toUpperCase()
            return { id: up, action: 'char', label: up }
        }
        if (/[0-9]/.test(key)) return { id: key, action: 'char', label: key }
        if (key === ' ') return { id: 'SPACE', action: 'space', label: ' ' }
    }
    if (key === 'Enter') return { id: 'ENTER', action: 'enter', label: '' }
    if (key === 'Backspace') return { id: 'BACK', action: 'back', label: '' }
    if (key === 'Shift') return { id: 'SHIFT', action: 'shift', label: '' }
    if (key === 'Escape') return { id: 'ESC', action: 'esc', label: '' }
    return null
}

const Console: FC = (): ReactNode => {
    const [phase, setPhase] = useState<Phase>('booting')
    const [bootText, setBootText] = useState('')
    const [input, setInput] = useState('')
    const [scrollback, setScrollback] = useState<ScrollEntry[]>([])
    const [activeCmd, setActiveCmd] = useState<Command | null>(null)
    const [galleryIndex, setGalleryIndex] = useState(0)
    const [pressed, setPressed] = useState<Set<string>>(new Set())
    const [lifeStyle, setLifeStyle] = useState<CSSProperties>({ opacity: 0 })
    const [respStep, setRespStep] = useState(0)
    const [bgHidden, setBgHidden] = useState(false)
    const [openSeq, setOpenSeq] = useState(0)

    // Refs mirroring state for use inside stable window listeners.
    const phaseRef = useRef<Phase>('booting')
    const inputRef = useRef('')
    const activeCmdRef = useRef<Command | null>(null)

    // Element refs.
    const sectionRef = useRef<HTMLElement | null>(null)
    const entranceRef = useRef<HTMLDivElement | null>(null)
    const screenRef = useRef<HTMLDivElement | null>(null)
    const screenInnerRef = useRef<HTMLDivElement | null>(null)
    const textRef = useRef<HTMLSpanElement | null>(null)

    // Intro / interaction refs.
    const typeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const finishTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const measureRaf = useRef<number | null>(null)
    const respTimers = useRef<ReturnType<typeof setTimeout>[]>([])
    const zoomAtEndRef = useRef(false)
    const committedRef = useRef(false)
    const lastSnark = useRef(-1)
    const currentPointerKey = useRef<string | null>(null)
    const lastTouch = useRef(0)

    useEffect(() => { phaseRef.current = phase }, [phase])
    useEffect(() => { inputRef.current = input }, [input])
    useEffect(() => { activeCmdRef.current = activeCmd }, [activeCmd])

    // ---------- selection helpers ----------
    const getSelOffsets = (): { start: number; end: number } | null => {
        const span = textRef.current
        if (!span || typeof window === 'undefined') return null
        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null
        const range = sel.getRangeAt(0)
        const cac = range.commonAncestorContainer
        if (cac !== span && !span.contains(cac)) return null
        const pre = document.createRange()
        pre.selectNodeContents(span)
        pre.setEnd(range.startContainer, range.startOffset)
        const a = pre.toString().length
        pre.setEnd(range.endContainer, range.endOffset)
        const b = pre.toString().length
        const len = inputRef.current.length
        const start = Math.max(0, Math.min(a, b))
        const end = Math.min(len, Math.max(a, b))
        return end > start ? { start, end } : null
    }

    const clearSel = (): void => {
        if (typeof window !== 'undefined') window.getSelection()?.removeAllRanges()
    }

    const selectAllText = (): void => {
        const span = textRef.current
        if (!span || typeof window === 'undefined') return
        const sel = window.getSelection()
        if (!sel) return
        const range = document.createRange()
        range.selectNodeContents(span)
        sel.removeAllRanges()
        sel.addRange(range)
    }

    const editText = (kind: 'insert' | 'delete', payload = ''): void => {
        const sel = getSelOffsets()
        setInput((prev) => {
            let next: string
            if (sel) {
                next = prev.slice(0, sel.start) + (kind === 'delete' ? '' : payload) + prev.slice(sel.end)
            } else if (kind === 'delete') {
                next = prev.slice(0, -1)
            } else {
                next = prev + payload
            }
            return next.slice(0, MAX_INPUT)
        })
        if (sel) clearSel()
    }

    // ---------- command pipeline ----------
    const pickSnark = (): string => {
        let i = Math.floor(Math.random() * SNARK.length)
        if (SNARK.length > 1) {
            while (i === lastSnark.current) i = Math.floor(Math.random() * SNARK.length)
        }
        lastSnark.current = i
        return SNARK[i]
    }

    const clearRespTimers = (): void => {
        respTimers.current.forEach(clearTimeout)
        respTimers.current = []
    }

    const runCommand = (cmd: Command): void => {
        clearRespTimers()
        setInput('')
        setGalleryIndex(0)
        setActiveCmd(cmd)
        activeCmdRef.current = cmd
        setRespStep(0)
        setPhase('responding')
        phaseRef.current = 'responding'
        respTimers.current = [
            setTimeout(() => setRespStep(1), 90),
            setTimeout(() => setRespStep(2), 540),
            setTimeout(() => setRespStep(3), 940),
            setTimeout(() => {
                setOpenSeq((n) => n + 1)
                setPhase('viewing')
                phaseRef.current = 'viewing'
            }, 1320)
        ]
    }

    const submit = (): void => {
        const raw = inputRef.current
        const trimmed = raw.trim()
        if (trimmed === '') return
        const lower = trimmed.toLowerCase()
        if ((COMMANDS as string[]).includes(lower)) {
            runCommand(lower as Command)
            return
        }
        const reply = pickSnark()
        setScrollback((prev) => [...prev, { cmd: raw, reply }].slice(-40))
        setInput('')
    }

    const closeWindow = (): void => {
        setActiveCmd(null)
        activeCmdRef.current = null
        setInput('')
        setPhase('idle')
        phaseRef.current = 'idle'
    }

    const handleEsc = (): void => {
        const p = phaseRef.current
        if (p === 'idle') {
            setInput('')
            clearSel()
        } else if (p === 'responding') {
            clearRespTimers()
            setActiveCmd(null)
            activeCmdRef.current = null
            setPhase('idle')
            phaseRef.current = 'idle'
        } else if (p === 'viewing') {
            closeWindow()
        }
    }

    const doAction = (action: KeyAction, label: string): void => {
        if (phaseRef.current === 'booting') return
        if (action === 'esc') {
            handleEsc()
            return
        }
        if (phaseRef.current !== 'idle') return
        switch (action) {
            case 'char':
                editText('insert', label)
                break
            case 'space':
                editText('insert', ' ')
                break
            case 'back':
                editText('delete')
                break
            case 'enter':
                if (getSelOffsets()) editText('insert', '\n')
                else submit()
                break
            default:
                break
        }
    }

    const pressKey = (id: string): void => {
        setPressed((prev) => {
            const n = new Set(prev)
            n.add(id)
            return n
        })
    }

    const releaseKey = (id: string): void => {
        setPressed((prev) => {
            const n = new Set(prev)
            n.delete(id)
            return n
        })
    }

    const galleryPrev = (): void => setGalleryIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length)
    const galleryNext = (): void => setGalleryIndex((i) => (i + 1) % GALLERY.length)

    // ---------- intro ----------
    const measure = (): ZoomData | null => {
        const section = sectionRef.current
        const screen = screenRef.current
        const entrance = entranceRef.current
        if (!section || !screen || !entrance) return null
        const sr = section.getBoundingClientRect()
        const scr = screen.getBoundingClientRect()
        const er = entrance.getBoundingClientRect()
        if (scr.width === 0 || scr.height === 0) return null
        const scale = Math.max(sr.width / scr.width, sr.height / scr.height)
        const screenCx = scr.left + scr.width / 2
        const screenCy = scr.top + scr.height / 2
        const sectionCx = sr.left + sr.width / 2
        const sectionCy = sr.top + sr.height / 2
        return {
            scale,
            dx: sectionCx - screenCx,
            dy: sectionCy - screenCy,
            originX: ((screenCx - er.left) / er.width) * 100,
            originY: ((screenCy - er.top) / er.height) * 100
        }
    }

    const finishIntro = (): void => {
        if (typeTimer.current) clearTimeout(typeTimer.current)
        if (holdTimer.current) clearTimeout(holdTimer.current)
        if (finishTimer.current) clearTimeout(finishTimer.current)
        setLifeStyle({ opacity: 1 })
        setPhase('idle')
        phaseRef.current = 'idle'
    }

    const zoomOut = (): void => {
        setLifeStyle((s) => ({ ...s, transition: 'transform 1.7s cubic-bezier(0.65, 0, 0.35, 1)' }))
        requestAnimationFrame(() =>
            requestAnimationFrame(() =>
                setLifeStyle((s) => ({ ...s, transform: 'translate(0px, 0px) scale(1)' }))
            )
        )
        finishTimer.current = setTimeout(finishIntro, 1750)
    }

    const startTyping = (zoomAtEnd: boolean): void => {
        zoomAtEndRef.current = zoomAtEnd
        const begin = (): void => {
            let i = 0
            const step = (): void => {
                i++
                setBootText(BOOT_SPLASH.slice(0, i))
                if (i >= BOOT_SPLASH.length) {
                    holdTimer.current = setTimeout(() => {
                        if (zoomAtEnd) zoomOut()
                        else finishIntro()
                    }, 1300)
                    return
                }
                const prevChar = BOOT_SPLASH[i - 1]
                const delay = prevChar === '\n' ? rand(150, 310) : rand(20, 46)
                typeTimer.current = setTimeout(step, delay)
            }
            typeTimer.current = setTimeout(step, rand(20, 46))
        }
        typeTimer.current = setTimeout(begin, 450)
    }

    const commitZoom = (m: ZoomData): void => {
        committedRef.current = true
        setLifeStyle({
            opacity: 1,
            transform: `translate(${m.dx}px, ${m.dy}px) scale(${m.scale})`,
            transformOrigin: `${m.originX}% ${m.originY}%`,
            transition: 'none'
        })
        startTyping(true)
    }

    const revealReduced = (): void => {
        committedRef.current = true
        zoomAtEndRef.current = false
        setLifeStyle({ opacity: 1 })
        setBootText(BOOT_SPLASH)
        holdTimer.current = setTimeout(finishIntro, 1300)
    }

    const revealFallback = (): void => {
        committedRef.current = true
        setLifeStyle({ opacity: 1 })
        startTyping(false)
    }

    const skip = (): void => {
        if (typeTimer.current) clearTimeout(typeTimer.current)
        if (holdTimer.current) clearTimeout(holdTimer.current)
        if (measureRaf.current) cancelAnimationFrame(measureRaf.current)
        setBootText(BOOT_SPLASH)
        if (committedRef.current && zoomAtEndRef.current) zoomOut()
        else finishIntro()
    }

    // Mount: measure & run the intro.
    useEffect(() => {
        const reduced =
            typeof window !== 'undefined' &&
            window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (reduced) {
            revealReduced()
            return () => {
                if (holdTimer.current) clearTimeout(holdTimer.current)
            }
        }

        let frames = 0
        let prevScale = 0
        const tick = (): void => {
            frames++
            const m = measure()
            if (m && m.scale >= 1.8 && Math.abs(m.scale - prevScale) < 0.02) {
                commitZoom(m)
                return
            }
            if (m) prevScale = m.scale
            if (frames < 180) measureRaf.current = requestAnimationFrame(tick)
            else revealFallback()
        }
        measureRaf.current = requestAnimationFrame(tick)

        return () => {
            if (measureRaf.current) cancelAnimationFrame(measureRaf.current)
            if (typeTimer.current) clearTimeout(typeTimer.current)
            if (holdTimer.current) clearTimeout(holdTimer.current)
            if (finishTimer.current) clearTimeout(finishTimer.current)
            clearRespTimers()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ---------- global input listeners ----------
    const onKeyDown = (e: KeyboardEvent): void => {
        if (isEditable(document.activeElement)) return
        if (phaseRef.current === 'booting') return

        if ((e.metaKey || e.ctrlKey) && (e.key === 'a' || e.key === 'A')) {
            if (phaseRef.current === 'idle') {
                e.preventDefault()
                selectAllText()
            }
            return
        }
        if (e.metaKey || e.ctrlKey || e.altKey) return

        if (phaseRef.current === 'viewing' && activeCmdRef.current === 'gallery') {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault()
                galleryPrev()
                return
            }
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault()
                galleryNext()
                return
            }
        }

        const mapped = mapPhysical(e.key)
        if (!mapped) return
        e.preventDefault()
        pressKey(mapped.id)
        doAction(mapped.action, mapped.label)
    }

    const onKeyUp = (e: KeyboardEvent): void => {
        if (isEditable(document.activeElement)) return
        const mapped = mapPhysical(e.key)
        if (mapped) releaseKey(mapped.id)
    }

    const onPointerUp = (): void => {
        const id = currentPointerKey.current
        if (id) {
            releaseKey(id)
            currentPointerKey.current = null
        }
    }

    const apiRef = useRef({ onKeyDown, onKeyUp, onPointerUp })
    apiRef.current = { onKeyDown, onKeyUp, onPointerUp }

    useEffect(() => {
        const kd = (e: KeyboardEvent): void => apiRef.current.onKeyDown(e)
        const ku = (e: KeyboardEvent): void => apiRef.current.onKeyUp(e)
        const up = (): void => apiRef.current.onPointerUp()
        window.addEventListener('keydown', kd)
        window.addEventListener('keyup', ku)
        window.addEventListener('mouseup', up)
        window.addEventListener('touchend', up)
        window.addEventListener('touchcancel', up)
        return () => {
            window.removeEventListener('keydown', kd)
            window.removeEventListener('keyup', ku)
            window.removeEventListener('mouseup', up)
            window.removeEventListener('touchend', up)
            window.removeEventListener('touchcancel', up)
        }
    }, [])

    // ---------- auto-scroll ----------
    useEffect(() => {
        const el = screenInnerRef.current
        if (!el) return
        if (phase === 'viewing') el.scrollTop = 0
        else el.scrollTop = el.scrollHeight
    }, [input, scrollback, respStep, phase, bootText, activeCmd])

    // ---------- pointer handlers on caps ----------
    const onCapMouseDown = (e: React.MouseEvent, k: KeyDef): void => {
        if (Date.now() - lastTouch.current < 400) return
        e.preventDefault()
        currentPointerKey.current = k.id
        pressKey(k.id)
        doAction(k.action, k.label)
    }

    const onCapTouchStart = (k: KeyDef): void => {
        lastTouch.current = Date.now()
        currentPointerKey.current = k.id
        pressKey(k.id)
        doAction(k.action, k.label)
    }

    const onTryClick = (e: React.MouseEvent<HTMLButtonElement>, c: Command): void => {
        e.currentTarget.blur()
        if (phaseRef.current === 'idle') runCommand(c)
    }

    const mode = phase === 'booting' ? 'idle' : phase

    return (
        <section className='console-hero' ref={sectionRef}>
            {!bgHidden && (
                <img
                    className='bg-img'
                    src={BG_SRC}
                    alt=''
                    aria-hidden='true'
                    draggable={false}
                    decoding='async'
                    onError={() => setBgHidden(true)}
                />
            )}
            <div className='grain' aria-hidden='true' />
            <div className='vignette' aria-hidden='true' />

            <div className={phase === 'viewing' ? 'stage is-viewing' : 'stage'}>
                <div className='entrance' ref={entranceRef} style={lifeStyle}>
                    <div className='life'>
                        <div className='computer'>
                            <div className='monitor'>
                                <div className='screen-bezel'>
                                    <div className='screen' ref={screenRef}>
                                        <div className='crt-glow' aria-hidden='true' />

                                        <div
                                            className='screen-inner'
                                            ref={screenInnerRef}
                                            data-mode={mode}
                                            data-booting={phase === 'booting' ? 'true' : 'false'}
                                        >
                                            {phase === 'booting' && (
                                                <div className='term-boot' aria-hidden='true'>
                                                    {bootText}
                                                    <span className='caret'>{CARET}</span>
                                                </div>
                                            )}

                                            {phase === 'idle' && (
                                                <div>
                                                    <div className='term-line term-head'>
                                                        Motion Terminal v1.0
                                                    </div>

                                                    <div className='term-line term-try'>
                                                        <span className='term-dim'>Try: </span>
                                                        {TRY_LIST.map((c, i) => (
                                                            <Fragment key={c}>
                                                                {i > 0 && <span className='term-dim'>, </span>}
                                                                <button
                                                                    type='button'
                                                                    className='term-cmd'
                                                                    onClick={(e) => onTryClick(e, c)}
                                                                >
                                                                    {c}
                                                                </button>
                                                            </Fragment>
                                                        ))}
                                                    </div>

                                                    {scrollback.map((entry, idx) => (
                                                        <div className='term-entry' key={idx}>
                                                            <div className='term-line'>
                                                                <span className='term-prompt'>{'>'}</span>{' '}
                                                                {entry.cmd}
                                                            </div>
                                                            <div className='term-line term-sys'>
                                                                {entry.reply}
                                                            </div>
                                                        </div>
                                                    ))}

                                                    <div className='term-line term-promptline'>
                                                        <span className='term-prompt'>{'>'}</span>{' '}
                                                        <span className='text-body' ref={textRef}>
                                                            {input}
                                                        </span>
                                                        <span className='caret'>{CARET}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {phase === 'responding' && (
                                                <div>
                                                    <div className='term-line term-head'>
                                                        Motion Terminal v1.0
                                                    </div>

                                                    {scrollback.map((entry, idx) => (
                                                        <div className='term-entry' key={idx}>
                                                            <div className='term-line'>
                                                                <span className='term-prompt'>{'>'}</span>{' '}
                                                                {entry.cmd}
                                                            </div>
                                                            <div className='term-line term-sys'>
                                                                {entry.reply}
                                                            </div>
                                                        </div>
                                                    ))}

                                                    <div className='term-entry' aria-hidden='true'>
                                                        <div className='term-line'>
                                                            <span className='term-prompt'>{'>'}</span>{' '}
                                                            {activeCmd}
                                                        </div>
                                                        {respStep >= 1 && (
                                                            <div className='term-line term-sys'>
                                                                Opening {activeCmd}.app...
                                                            </div>
                                                        )}
                                                        {respStep >= 2 && (
                                                            <div className='term-line term-sys'>
                                                                Loading components...
                                                            </div>
                                                        )}
                                                        {respStep >= 3 && (
                                                            <div className='term-line term-sys'>Ready.</div>
                                                        )}
                                                        <div className='term-line'>
                                                            <span className='caret'>{CARET}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {phase === 'viewing' && activeCmd && (
                                                <AppWindow
                                                    key={`${activeCmd}-${openSeq}`}
                                                    command={activeCmd}
                                                    galleryIndex={galleryIndex}
                                                    onGalleryPrev={galleryPrev}
                                                    onGalleryNext={galleryNext}
                                                    onClose={closeWindow}
                                                />
                                            )}
                                        </div>

                                        <div className='crt-roll' aria-hidden='true' />
                                        <div className='scanlines' aria-hidden='true' />
                                        <div className='curvature' aria-hidden='true' />
                                        <div className='crt-glass' aria-hidden='true' />
                                    </div>
                                </div>

                                <div className='mon-face'>
                                    <span className='mon-brand'>MotionVision</span>
                                    <div className='mon-vent' aria-hidden='true'>
                                        <span />
                                        <span />
                                        <span />
                                    </div>
                                    <div className='mon-power' aria-hidden='true'>
                                        <span className='mon-led' />
                                    </div>
                                </div>
                            </div>

                            <div className='mon-neck' aria-hidden='true' />
                            <div className='mon-base' aria-hidden='true' />

                            <div className='keyboard-tilt'>
                                <div className='keyboard'>
                                    {KEY_ROWS.map((row, ri) => (
                                        <div className='row' key={ri}>
                                            {row.map((k) => (
                                                <div
                                                    key={k.id}
                                                    role='button'
                                                    aria-label={k.id}
                                                    tabIndex={-1}
                                                    className={`key k-${k.color}`}
                                                    data-pressed={pressed.has(k.id) ? 'true' : 'false'}
                                                    style={{ flex: `0 0 calc(var(--ku) * ${k.w})` }}
                                                    onMouseDown={(e) => onCapMouseDown(e, k)}
                                                    onTouchStart={() => onCapTouchStart(k)}
                                                >
                                                    {k.label}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className='shadow-wrap' aria-hidden='true'>
                            <div className='contact-shadow' />
                        </div>
                    </div>
                </div>
            </div>

            {phase === 'booting' && (
                <button type='button' className='boot-skip' onClick={skip}>
                    click to skip
                </button>
            )}
        </section>
    )
}

export default Console
